// Responsive-image helpers — provider-aware URL rewriting + srcset
// generation for the public site. Hooked into <Img preset="hero|card|
// thumbnail|inline"> in src/components/App.jsx so every render site
// gets sensibly-sized variants without per-call boilerplate.
//
// 2026-05-19c — was serving 6000×3991 originals to mobile clients
// (e.g. /dest/antiparos hero was ~3MB on a 768px viewport).

export function detectProvider(url) {
  if (!url || typeof url !== 'string') return null;
  if (url.startsWith('data:')) return 'data';
  try {
    const h = new URL(url, 'https://localhost').hostname;
    if (h.includes('cdn.sanity.io')) return 'sanity';
    if (h.includes('images.unsplash.com')) return 'unsplash';
    if (h.includes('images.pexels.com')) return 'pexels';
    return 'other';
  } catch {
    return null;
  }
}

// Build a single-width URL with provider-specific transform params.
// Always returns a valid URL — if the provider doesn't support
// resizing we pass through unchanged. Strips existing query so the
// caller's earlier `?w=800&q=80...` doesn't double up.
// quality default 70 — visually-lossless for photographic webp/avif on the
// public site (heroes sit under a navy gradient; cards/thumbnails are small),
// ~18% lighter than q80 across every Sanity/Unsplash variant site-wide. Applies
// to all presets (hero/card/thumbnail/inline/avatar) AND the React <Img> +
// SSR static-hero blocks alike — they all route through this one builder, so a
// single default keeps the <img> and its <link rel=preload> byte-identical
// (one fetch, never a q-mismatch double-download).
export function buildResponsiveUrl(url, { width, quality = 70, format = 'auto' } = {}) {
  if (!url) return null;
  const provider = detectProvider(url);
  if (provider === 'data' || provider === 'other' || provider === null) return url;
  const base = url.split('?')[0];
  if (provider === 'sanity') {
    const p = new URLSearchParams();
    if (width) p.set('w', String(width));
    p.set('auto', 'format');
    p.set('q', String(quality));
    p.set('fit', 'max');
    return base + '?' + p.toString();
  }
  if (provider === 'unsplash') {
    const p = new URLSearchParams();
    if (width) p.set('w', String(width));
    p.set('auto', 'format');
    p.set('fit', 'crop');
    p.set('q', String(quality));
    return base + '?' + p.toString();
  }
  if (provider === 'pexels') {
    const p = new URLSearchParams();
    if (width) p.set('w', String(width));
    p.set('auto', 'compress');
    p.set('cs', 'tinysrgb');
    return base + '?' + p.toString();
  }
  return url;
}

// Build `srcset` from an array of widths.
export function buildSrcset(url, widths, opts = {}) {
  if (!url || !Array.isArray(widths) || widths.length === 0) return null;
  const provider = detectProvider(url);
  if (provider === 'data' || provider === 'other' || provider === null) return null;
  return widths
    .map(w => buildResponsiveUrl(url, { ...opts, width: w }) + ' ' + w + 'w')
    .join(', ');
}

// Sizing presets for the four common contexts. Each preset gives:
//   - widths      → srcset variants
//   - sizes       → matching `sizes` attribute
//   - defaultWidth → fallback `src` width when DPR/viewport unknown
//   - loading     → loading attribute
//   - fetchpriority → fetchpriority hint
export const SIZE_PRESETS = {
  hero: {
    // 768 fills the 640→960 gap so a 100vw hero on a ~412px mobile viewport at
    // DPR ~1.75 (412×1.75≈721) selects the 768w variant instead of overshooting
    // to 960w — ~36% fewer pixels, a meaningful drop in the (load-bound) hero
    // download / LCP load-duration. Higher-DPR phones still pick 960+. The
    // <img> srcset and the <head> preload imagesrcset both derive from this
    // list, so they stay byte-identical (one fetch, preload-matched).
    widths: [640, 768, 960, 1280, 1600, 1920, 2560],
    sizes: '100vw',
    defaultWidth: 1600,
    loading: 'eager',
    fetchpriority: 'high',
  },
  card: {
    // 720 fills the 640→800 gap: a full-width mobile card (~378–412px CSS) at
    // DPR ~1.75 needs ~660–720px and was overshooting to 800w; 720 lets it pick
    // tighter. Higher-DPR / true-full-width still reach 800.
    widths: [320, 480, 640, 720, 800],
    sizes: '(max-width: 768px) 100vw, (max-width: 1280px) 50vw, 400px',
    defaultWidth: 640,
    loading: 'lazy',
    fetchpriority: 'auto',
    // Intrinsic-ratio hint (CLS): card containers enforce 3:2 via CSS
    // (aspect-ratio / fixed boxes) — width/height attributes matching that
    // ratio let the browser reserve the box BEFORE the image loads. Attributes
    // never override the container CSS (width/height 100% + object-fit).
    aspect: 1.5,
  },
  thumbnail: {
    widths: [200, 320, 480],
    sizes: '(max-width: 480px) 50vw, 200px',
    defaultWidth: 320,
    loading: 'lazy',
    fetchpriority: 'auto',
    aspect: 1.5,
  },
  // Tiny decorative circle (route-card origin/dest avatars, ~36–40px rendered).
  // The `card` preset's sizes="100vw" made mobile pull the 800w variant for a
  // 36px circle (~80 KiB each, dozens per listing) — the bulk of the page's
  // "Improve image delivery" savings. 48px sizes + small widths serve ~96–160px
  // (≈2–5 KiB) and stay crisp to DPR 3. loading=lazy (always below the fold).
  avatar: {
    widths: [96, 160, 224],
    sizes: '48px',
    defaultWidth: 160,
    loading: 'lazy',
    fetchpriority: 'auto',
    aspect: 1,
  },
  inline: {
    widths: [640, 960, 1280],
    sizes: '(max-width: 768px) 100vw, 800px',
    defaultWidth: 960,
    loading: 'lazy',
    fetchpriority: 'auto',
  },
};
